// 광고 (§13). 스토어 앱(Capacitor 네이티브) = AdMob 전면/리워드. 웹 = 호출부에서 목/AdSense.
// 실 광고 표시는 cap add ios/android + 기기/시뮬레이터 필요. 웹/dev에선 no-op(목 화면이 대신).
// 실 광고 단위 ID는 .env(VITE_ADMOB_*)로 교체. 없으면 구글 공식 테스트 ID + 테스트 모드.

import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import {
  AdMob,
  AdmobConsentStatus,
  BannerAdPluginEvents,
  BannerAdPosition,
  BannerAdSize,
  InterstitialAdPluginEvents,
  RewardAdPluginEvents,
} from "@capacitor-community/admob";

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}
function isIOS(): boolean {
  return Capacitor.getPlatform() === "ios";
}

// 구글 공식 테스트 광고 단위 ID.
const TEST = {
  interstitial: {
    ios: "ca-app-pub-3940256099942544/4411468910",
    android: "ca-app-pub-3940256099942544/1033173712",
  },
  rewarded: {
    ios: "ca-app-pub-3940256099942544/1712485313",
    android: "ca-app-pub-3940256099942544/5224354917",
  },
  banner: {
    ios: "ca-app-pub-3940256099942544/2934735716",
    android: "ca-app-pub-3940256099942544/6300978111",
  },
};

const env = (typeof import.meta !== "undefined" && import.meta.env) || ({} as Record<string, string>);
// 강제 테스트(VITE_ADMOB_TESTING): 실 ID여도 테스트 광고만 띄움 — 개발 중 자기클릭(무효 트래픽) 계정정지 방지.
const FORCE_TEST = env.VITE_ADMOB_TESTING === "1" || env.VITE_ADMOB_TESTING === "true";

// ── 원격 광고 설정(서버 GET /api/config) ──────────────────────────
// 광고 유닛 ID를 빌드에 박지 않고 런타임에 받아 쓴다 → 심사 후 앱 재제출 없이 서버에서 ID만 교체.
// (App Store 2.3.1: 게이트 구조·노출 양·위치는 코드에 고정, 원격으로 바뀌는 건 '수익 ID'뿐.)
// 누락/실패면 build-time env → 구글 공식 테스트 ID로 안전 폴백.
const CONFIG_URL = env.VITE_CONFIG_ENDPOINT || "https://bwek.app/api/config";
type IdPair = { ios?: string; android?: string };
let remoteAds: { interstitial?: IdPair; rewarded?: IdPair } | null = null;

async function fetchAdConfig(): Promise<void> {
  try {
    const res = await fetch(CONFIG_URL, { cache: "no-store" });
    if (!res.ok) return;
    const j = (await res.json()) as { ads?: { interstitial?: IdPair; rewarded?: IdPair } };
    if (j && j.ads) remoteAds = j.ads;
  } catch {
    /* 네트워크 실패 — 폴백 ID 사용 */
  }
}

// 해당 ID가 구글 공식 테스트 단위(퍼블리셔 3940256099942544)면 테스트 모드로 띄운다.
function isTestId(id: string): boolean {
  return id.includes("3940256099942544");
}
function testingFor(id: string): boolean {
  return FORCE_TEST || isTestId(id);
}

// 앱 ID(~ 포함): 광고 호출엔 안 쓰임(네이티브 config가 읽음). cap add 후 Info.plist/Manifest 주입용으로 노출.
export const ADMOB_APP_ID = {
  ios: env.VITE_ADMOB_APP_ID_IOS || "",
  android: env.VITE_ADMOB_APP_ID_ANDROID || "",
};

// §13: 긴 분노 = 긴(리워드) 광고. 길이 임계는 출발점이고 튜닝 대상.
export const LONG_RANT_CHARS = 280;
export function isLongRant(charCount: number): boolean {
  return charCount >= LONG_RANT_CHARS;
}

// ID 우선순위: 원격(서버) → build-time env → 구글 테스트 ID.
function interstitialId(): string {
  const r = remoteAds?.interstitial;
  return isIOS()
    ? r?.ios || env.VITE_ADMOB_INTERSTITIAL_IOS || TEST.interstitial.ios
    : r?.android || env.VITE_ADMOB_INTERSTITIAL_ANDROID || TEST.interstitial.android;
}
function rewardedId(): string {
  const r = remoteAds?.rewarded;
  return isIOS()
    ? r?.ios || env.VITE_ADMOB_REWARDED_IOS || TEST.rewarded.ios
    : r?.android || env.VITE_ADMOB_REWARDED_ANDROID || TEST.rewarded.android;
}
function bannerId(): string {
  return isIOS()
    ? env.VITE_ADMOB_BANNER_IOS || TEST.banner.ios
    : env.VITE_ADMOB_BANNER_ANDROID || TEST.banner.android;
}

let initialized = false;

// 전면광고 미리 로드 상태 — 호출 시점에 로드하면 1~3초 빈 화면이 보이므로 앞당겨 준비해둔다.
let interstitialReady = false;
let preparing: Promise<void> | null = null;

/** 전면광고를 미리 로드(중복 호출 안전). 표시 후 다음 것을 위해 다시 호출. */
function preloadInterstitial(): Promise<void> {
  if (!isNative() || interstitialReady) return Promise.resolve();
  if (!preparing) {
    const adId = interstitialId();
    preparing = AdMob.prepareInterstitial({ adId, isTesting: testingFor(adId) })
      .then(() => {
        interstitialReady = true;
      })
      .catch(() => {
        /* fill 실패 등 — 표시 때 다시 시도 */
      })
      .finally(() => {
        preparing = null;
      });
  }
  return preparing;
}

/** 앱 시작 시 1회: AdMob 초기화 + UMP 동의 + iOS ATT + 전면광고 미리 로드. 웹에선 no-op. */
export async function initAds(): Promise<void> {
  if (!isNative() || initialized) return;
  initialized = true;
  await fetchAdConfig(); // ★원격 광고 ID 먼저 받아온다(없으면 build-time env → 구글 테스트ID 폴백)
  try {
    await AdMob.initialize({ initializeForTesting: testingFor(interstitialId()) });
    // UMP 동의(EEA/UK 필수, 글로벌 안전).
    const info = await AdMob.requestConsentInfo();
    if (info.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) {
      await AdMob.showConsentForm();
    }
    // iOS ATT(맞춤광고 권한).
    if (isIOS()) await AdMob.requestTrackingAuthorization();
  } catch {
    /* 초기화 실패해도 앱은 동작(광고만 안 뜸). */
  }
  void preloadInterstitial(); // 첫 전면광고 미리 준비(사용자가 도달할 즈음엔 ready)
}

let bannerRetryTimer: ReturnType<typeof setTimeout> | null = null;
let bannerFailHandle: PluginListenerHandle | null = null;

async function stopBannerRetry(): Promise<void> {
  if (bannerRetryTimer) {
    clearTimeout(bannerRetryTimer);
    bannerRetryTimer = null;
  }
  if (bannerFailHandle) {
    await bannerFailHandle.remove();
    bannerFailHandle = null;
  }
}

/** 배너 표시(홈 화면, §13). marginTop(CSS px≈dp; 플러그인이 ×density로 px 변환)을 주면 상단 기준
 *  그 위치에 오버레이(노트 바로 아래), 없으면 화면 하단. 적응형 배너. 웹/실패면 no-op.
 *  콜드 스타트·일시적 fill 실패에 대비해 로드 실패 시 재시도(최대 4회). */
export async function showBottomBanner(marginTop = 0): Promise<void> {
  if (!isNative()) return;
  await stopBannerRetry();
  const opts = {
    adId: bannerId(),
    adSize: BannerAdSize.ADAPTIVE_BANNER,
    position: marginTop > 0 ? BannerAdPosition.TOP_CENTER : BannerAdPosition.BOTTOM_CENTER,
    margin: marginTop,
    isTesting: testingFor(bannerId()),
  };
  const show = () => {
    AdMob.showBanner(opts).catch(() => {});
  };
  let attempts = 0;
  bannerFailHandle = await AdMob.addListener(BannerAdPluginEvents.FailedToLoad, () => {
    if (attempts >= 4) return;
    attempts += 1;
    bannerRetryTimer = setTimeout(show, 2500);
  });
  show();
}

/** 배너 제거(홈을 벗어날 때). 재시도 타이머·리스너도 정리. */
export async function hideBottomBanner(): Promise<void> {
  if (!isNative()) return;
  await stopBannerRetry();
  try {
    await AdMob.removeBanner();
  } catch {
    /* 이미 없을 수 있음 */
  }
}

/** 전면광고: 미리 로드된 광고를 즉시 표시 → 닫힘까지 대기. 표시 후 다음 광고 미리 준비.
 *  웹/미준비/실패면 즉시 resolve(루프 안 끊김). */
export async function showInterstitial(): Promise<void> {
  if (!isNative()) return;
  try {
    if (!interstitialReady) await preloadInterstitial(); // 아직 안 됐으면 지금 로드(도달이 너무 빨랐을 때)
    if (!interstitialReady) return; // 로드 실패 → 막지 않고 진행
    interstitialReady = false; // 소비
    const handles: PluginListenerHandle[] = [];
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        handles.forEach((h) => h.remove());
        clearTimeout(safety);
        resolve();
      };
      const safety = setTimeout(finish, 20000); // 닫힘 이벤트 누락 대비
      Promise.all([
        AdMob.addListener(InterstitialAdPluginEvents.Dismissed, finish),
        AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, finish),
      ]).then((hs) => {
        handles.push(...hs);
        if (settled) hs.forEach((h) => h.remove());
      });
      AdMob.showInterstitial().catch(finish);
    });
  } catch {
    /* 실패해도 진행 */
  } finally {
    void preloadInterstitial(); // 다음 전면광고 미리 준비
  }
}

// 리워드 결과: earned=끝까지 봄(보상) · dismissed=중간에 닫음(보상 없음) · unavailable=로드/표시 실패.
// 호출부는 unavailable이면 "게이트 통과"시켜 광고 인프라 문제로 사용자를 막지 않는다(§13).
export type RewardResult = "earned" | "dismissed" | "unavailable";

/** 리워드 광고(export 게이트, §13). */
export async function showRewarded(): Promise<RewardResult> {
  if (!isNative()) return "unavailable";
  try {
    const adId = rewardedId();
    await AdMob.prepareRewardVideoAd({ adId, isTesting: testingFor(adId) });
  } catch {
    return "unavailable"; // 로드 실패 → 막지 않음
  }
  const handles: PluginListenerHandle[] = [];
  return await new Promise<RewardResult>((resolve) => {
    let earned = false;
    let shown = false;
    let settled = false;
    const finish = (r: RewardResult) => {
      if (settled) return;
      settled = true;
      handles.forEach((h) => h.remove());
      clearTimeout(safety);
      resolve(r);
    };
    // 표시 자체가 안 됐으면 unavailable, 표시됐는데 닫혔으면 earned/dismissed.
    const safety = setTimeout(() => finish(shown ? (earned ? "earned" : "dismissed") : "unavailable"), 60000);
    Promise.all([
      AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
        earned = true;
      }),
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish(earned ? "earned" : "dismissed")),
      AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => finish("unavailable")),
    ]).then((hs) => {
      handles.push(...hs);
      if (settled) hs.forEach((h) => h.remove());
    });
    AdMob.showRewardVideoAd()
      .then(() => {
        shown = true;
      })
      .catch(() => finish("unavailable"));
  });
}
